<script lang="ts">
export default {
  name: 'PaymentMethodList',
}
</script>
<script setup lang="ts" >
import { KkIcon, KkLoading } from '../../../../components/base'
import { KkPreviewModal } from './../../index'
import type { PaymentMethodPreview } from 'src/biz_spa/domains/paymentMethod/types'
import { useI18n } from 'vue-i18n'
import { computed, ref } from 'vue'
import {
  useRoute
} from 'vue-router'
const { t } = useI18n()
const route = useRoute()
const showPreview = ref(false)
const currentCompanyUuid = computed<string>(() => route.params.company_uuid as string)
const currentStoreUuid = computed<string>(() => route.params.store_uuid as string)
interface PaymentMethod {
  creditCard: CreditCard
  qrCodePayment: QrCodePayment
  electronicMoney: ElectronicMoney
  transportationElectronicMoney: TransportationElectronicMoney
  cashOnly: {
    available: string
  }
  debitCard: {
    available: string
  }
  payCheck: {
    available: string
  }
}
interface CreditCard {
  available: string
  americanExpressAvailable: string
  discoverAvailable: string
  dinersClubAvailable: string
  jcbAvailable: string
  mastercardAvailable: string
  unionPayAvailable: string
  visaAvailable: string
}
interface QrCodePayment {
  available: string
  auPay: string
  dPay: string,
  paypay: string,
  rakutenPay: string,
  weChatPay: string,
  alipay: string,
}
interface ElectronicMoney {
  available: string
  idElectronicMoney: string,
  rakutenEdy: string,
  quicPay: string,
  waon: string,
  nanaco: string,
  auWallet: string,
  applePay: string,
}
interface TransportationElectronicMoney {
  available: string
  hayakaken: string,
  icoca: string,
  kitaca: string,
  nimoca: string,
  pasmo: string,
  pitapa: string,
  sapica: string,
  sugoca: string,
  suica: string,
  toica: string,
  manaca: string,
}
interface Props {
  paymentMethodSynced?: boolean
  loading: boolean
  previewLoading: boolean
  paymentMethod?: PaymentMethod
  paymentMethodPreview: PaymentMethodPreview
  canChange: boolean
  linkedSites: string[]
}
const emit = defineEmits(['show-preview'])
const props = defineProps<Props>()
const availableCreditCardNames = computed<string>(() => {
  const creditCards = props.paymentMethod?.creditCard
  if (!creditCards) return ''

  return Object.entries(creditCards)
    .filter(card => card[0] !== 'available')
    .filter(card => card[1] === 'ok')
    .map(card => t(`models.storePaymentMethod.creditCards.${card[0]}`)).join(' / ')
})
const availableElectronicMoneyNames = computed<string>(() => {
  const electronicMoney = props.paymentMethod?.electronicMoney
  if (!electronicMoney) return ''

  return Object.entries(electronicMoney)
    .filter(em => em[0] !== 'available')
    .filter(em => em[1] === 'ok')
    .map(em => t(`models.storePaymentMethod.electronicMoneys.${em[0]}`)).join(' / ')
})
const availableTransportationElectronicMoneyNames = computed<string>(() => {
  const transportationElectronicMoney = props.paymentMethod?.transportationElectronicMoney
  if(!transportationElectronicMoney) return ''

  return Object.entries(transportationElectronicMoney)
    .filter(tem => tem[0] !== 'available')
    .filter(tem => tem[1] === 'ok')
    .map(tem => t(`models.storePaymentMethod.transportationElectronicMoneys.${tem[0]}`)).join(' / ')
})
const availableQrCodePaymentNames = computed<string>(() => {
  const qrCodePayments = props.paymentMethod?.qrCodePayment
  if(!qrCodePayments) return ''

  return Object.entries(qrCodePayments)
    .filter(qr => qr[0] !== 'available')
    .filter(qr => qr[1] === 'ok')
    .map(qr => t(`models.storePaymentMethod.qrCodePayments.${qr[0]}`)).join(' / ')
})
const creditCardAvailable = computed<string>(() => {
  return props.paymentMethod ? props.paymentMethod.creditCard.available : 'na'
})
const electronicMoneyAvailable = computed<string>(() => {
  return props.paymentMethod ? props.paymentMethod.electronicMoney.available : 'na'
})
const transportationElectronicMoneyAvailable = computed<string>(() => {
  return props.paymentMethod ? props.paymentMethod.transportationElectronicMoney.available : 'na'
})
const qrCodePaymentAvailable = computed<string>(() => {
  return props.paymentMethod ? props.paymentMethod.qrCodePayment.available : 'na'
})
const cashOnlyAvailable = computed<string>(() => {
  return props.paymentMethod ? props.paymentMethod.cashOnly.available : 'na'
})
const debitCardAvailable = computed<string>(() => {
  return props.paymentMethod ? props.paymentMethod.debitCard.available : 'na'
})
const payCheckAvailable = computed<string>(() => {
  return props.paymentMethod ? props.paymentMethod.payCheck.available : 'na'
})
const handleShowPreview = (): void => {
  showPreview.value = true
  emit('show-preview')
}

</script>

<template>
  <div class="c-grid__item c-grid__item__s__12 c-grid__item__m__6 c-grid__item__l__6">
    <section class="p-box">
      <header class="p-box__header">
        <h3 class="p-box__title">
          {{ $t('models.storePaymentMethod.modelName') }}
          <KkIcon v-show="props.paymentMethodSynced" name="protection" color="#79acb0" />
        </h3>
      </header>
      <div class="p-box__body">
        <KkLoading v-if="loading" />
        <template v-else>
          <div class="paymentMethod__section">
            <div class="paymentMethod__header">
              <span class="paymentMethod__title">{{ t('models.storePaymentMethod.creditCard') }}</span>
              <KkIcon
                v-if="creditCardAvailable !== 'na'"
                class="paymentMethod__icon"
                :name="creditCardAvailable === 'ok' ? 'iconOk' : 'iconNg'"
              />
            </div>
            {{ availableCreditCardNames }}
          </div>
          <div class="paymentMethod__section">
            <div class="paymentMethod__header">
              <span class="paymentMethod__title">{{ t('models.storePaymentMethod.electronicMoney') }}</span>
              <KkIcon
                v-if="electronicMoneyAvailable !== 'na'"
                class="paymentMethod__icon"
                :name="electronicMoneyAvailable === 'ok' ? 'iconOk' : 'iconNg'"
              />
            </div>
            {{ availableElectronicMoneyNames }}
          </div>
          <div class="paymentMethod__section">
            <div class="paymentMethod__header">
              <span class="paymentMethod__title">{{ t('models.storePaymentMethod.transportationElectronicMoney') }}</span>
              <KkIcon
                v-if="transportationElectronicMoneyAvailable !== 'na'"
                class="paymentMethod__icon"
                :name="transportationElectronicMoneyAvailable === 'ok' ? 'iconOk' : 'iconNg'"
              />
            </div>
            {{ availableTransportationElectronicMoneyNames }}
          </div>
          <div class="paymentMethod__section">
            <div class="paymentMethod__header">
              <span class="paymentMethod__title">{{ t('models.storePaymentMethod.qrCodePayment') }}</span>
              <KkIcon
                v-if="qrCodePaymentAvailable !== 'na'"
                class="paymentMethod__icon"
                :name="qrCodePaymentAvailable === 'ok' ? 'iconOk' : 'iconNg'"
              />
            </div>
            {{ availableQrCodePaymentNames }}
          </div>
          <div class="paymentMethod__section">
            <div class="paymentMethod__header">
              <span class="paymentMethod__title">{{ t('models.storePaymentMethod.cashOnly') }}</span>
              <KkIcon
                v-if="cashOnlyAvailable !== 'na'"
                class="paymentMethod__icon"
                :name="cashOnlyAvailable === 'ok' ? 'iconOk' : 'iconNg'"
              />
            </div>
          </div>
          <div class="paymentMethod__section">
            <div class="paymentMethod__header">
              <span class="paymentMethod__title">{{ t('models.storePaymentMethod.debitCardAvailable') }}</span>
              <KkIcon
                v-if="debitCardAvailable !== 'na'"
                class="paymentMethod__icon"
                :name="debitCardAvailable === 'ok' ? 'iconOk' : 'iconNg'"
              />
            </div>
          </div>
          <div class="paymentMethod__section">
            <div class="paymentMethod__header">
              <span class="paymentMethod__title">{{ t('models.storePaymentMethod.checkAvailable') }}</span>
              <KkIcon
                v-if="payCheckAvailable !== 'na'"
                class="paymentMethod__icon"
                :name="payCheckAvailable === 'ok' ? 'iconOk' : 'iconNg'"
              />
            </div>
          </div>
          <div v-if="canChange" class="c-button c-button--right">
            <span
              v-if="linkedSites.length > 0"
              class="previewLink"
              @click="handleShowPreview"
            >
              {{ $t('models.storePaymentMethod.preview') }}
            </span>
            <router-link :to="{ name: 'CompaniesLocationStorePaymentMethodsEdit', params: { company_uuid: currentCompanyUuid, store_uuid: currentStoreUuid } }" class="c-button__item c-button__item--primary">
              {{ $t('labels.change') }}
            </router-link>
          </div>
          <KkPreviewModal
            :is-visible="showPreview"
            :loading="previewLoading"
            :show-submit="false"
            :title="$t('models.storePaymentMethod.previewTitle')"
            :payment-method-synced="props.paymentMethodSynced || false"
            :payment-method-preview="props.paymentMethodPreview"
            @close="showPreview=false"
            @submit="() => {}"
          />
        </template>
      </div>
    </section>
  </div>
</template>

<style lang="scss" scoped>
@import '../../../../../../stylesheets/biz_spa/foundation/_var.scss';
.paymentMethod {
  &__section {
    margin-bottom: 20px;
    padding-bottom: 20px;
    border-bottom: 1px solid #e4e7eb;
  }
  &__header {
    display: flex;
    align-items: center;
  }
  &__title {
    font-weight: bold;
    margin-right: 5px;
  }
  &__icon {
    width: 15px;
  }
  &-card {
    &-label {
      display: flex;
      align-items: center;
      margin: 12px 0;
      &__img {
        margin-right: 8px;
      }
    }
  }
}
.p-box__title {
  display: flex;
  align-items: center;
  svg {
    margin-left: 5px;
    width: 15px;
  }
}

.previewLink {
  color: $color-accent-primary;
  text-decoration: underline;
  cursor: pointer;
}
</style>
